import React, { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NOTE_MAX = 600;

function GroupList({ groups }) {
  return (
    <ul className="divide-y divide-zinc-200 border border-zinc-300 text-xs" data-testid="email-pack-groups">
      {groups.map((g) => (
        <li key={g.client_id || "none"} className="flex flex-wrap items-center gap-2 px-2 py-1.5" data-testid={`email-pack-group-${g.client_id || "none"}`}>
          <b>{g.client_name}</b>
          <span className="text-zinc-600">{g.leads} lead{g.leads === 1 ? "" : "s"}</span>
          {g.problem
            ? <span className="ml-auto text-red-700" data-testid={`email-pack-problem-${g.client_id || "none"}`}>{g.problem}</span>
            : <span className="ml-auto font-mono text-zinc-700">{g.to}</span>}
        </li>
      ))}
    </ul>
  );
}

function resultToast(results) {
  const sent = results.filter((r) => r.status === "sent").length;
  const other = results.length - sent;
  const msg = `Proof pack emailed to ${sent} buyer${sent === 1 ? "" : "s"}${other ? ` · ${other} not sent` : ""}`;
  (sent ? toast.success : toast.error)(msg);
}

// Bad Leads queue: email each buyer a 7-day download link to the proof pack for their own disputed leads.
export function EmailPackButton({ reportIds }) {
  const { user, hasPerm } = useAuth();
  const [plan, setPlan] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.org_type !== "internal" || !hasPerm("export_leads") || !reportIds.length) return null;
  const call = (dryRun) => api.post("/bad-leads/proof-pack-email", { report_ids: reportIds, note, dry_run: dryRun });
  const fail = (e) => toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Couldn't prepare the email");
  const start = async () => {
    setBusy(true);
    try { setNote(""); setPlan((await call(true)).data); } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { resultToast((await call(false)).data.results); setPlan(null); } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const ready = (plan?.groups || []).filter((g) => !g.problem).length;
  return (
    <>
      <button type="button" onClick={start} disabled={busy} data-testid="badlead-email-pack-btn"
        className="inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-40">
        <EnvelopeSimple size={12} weight="bold" /> Email proof pack
      </button>
      <Dialog open={Boolean(plan)} onOpenChange={(v) => { if (!v) setPlan(null); }}>
        <DialogContent className="max-w-lg" data-testid="email-pack-dialog">
          <DialogHeader>
            <DialogTitle>Email proof pack</DialogTitle>
            <DialogDescription>
              Each buyer gets their own email with only their leads and a download link that works for {plan?.link_days} days.
              {plan?.copy_to ? ` A copy goes to ${plan.copy_to}.` : ""}
            </DialogDescription>
          </DialogHeader>
          {plan && <GroupList groups={plan.groups} />}
          <Textarea value={note} maxLength={NOTE_MAX} rows={4} onChange={(e) => setNote(e.target.value)}
            placeholder="Short note to the buyer (optional, no links)" data-testid="email-pack-note" />
          <div className="text-right text-[10px] text-zinc-500">{note.length}/{NOTE_MAX}</div>
          <DialogFooter>
            <button type="button" onClick={() => setPlan(null)} data-testid="email-pack-cancel"
              className="border border-black px-3 py-2 text-xs uppercase tracking-widest transition-colors duration-100 hover:bg-zinc-100">Cancel</button>
            <button type="button" onClick={send} disabled={busy || !ready} data-testid="email-pack-send"
              className="border-2 border-black bg-black px-3 py-2 text-xs uppercase tracking-widest text-white transition-colors duration-100 hover:bg-zinc-800 disabled:opacity-40">
              {busy ? "Sending…" : `Send to ${ready} buyer${ready === 1 ? "" : "s"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
